import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { User } from '../entities/user.model';

@Injectable({
  providedIn: 'root'
})
export class DisplayService {
  private textSizeSubject = new BehaviorSubject<1 | 2 | 3 | 4 | 5>(3); // Default to medium
  private darkModeSubject = new BehaviorSubject<boolean>(true); // Default to dark mode (original theme)
  
  public textSize$ = this.textSizeSubject.asObservable();
  public darkMode$ = this.darkModeSubject.asObservable();

  constructor() {
    // Apply initial styling to ensure default display works
    this.applyDarkMode(this.darkModeSubject.value);
    this.applyTextSize(this.textSizeSubject.value);
  }

  /**
   * Update display preferences from user model
   */
  updateFromUser(user: User | null): void {
    if (user) {
      const textSize = user.textSize || 3;
      const darkMode = user.darkMode !== undefined ? user.darkMode : true; // Default to true (dark mode)
      
      this.setTextSize(textSize);
      this.setDarkMode(darkMode);
    } else {
      // Reset to defaults for anonymous users (original dark theme)
      this.setTextSize(3);
      this.setDarkMode(true); // Keep original dark theme
    }
  }

  /**
   * Set text size preference
   */
  setTextSize(size: 1 | 2 | 3 | 4 | 5): void {
    console.log(`Setting text size to: ${size}`);
    this.textSizeSubject.next(size);
    this.applyTextSize(size);
  }

  /**
   * Set dark mode preference
   */
  setDarkMode(enabled: boolean): void {
    console.log(`Setting dark mode to: ${enabled}`);
    this.darkModeSubject.next(enabled);
    this.applyDarkMode(enabled);
  }

  /**
   * Get current text size
   */
  getCurrentTextSize(): 1 | 2 | 3 | 4 | 5 {
    return this.textSizeSubject.value;
  }

  /**
   * Get current dark mode state
   */
  getCurrentDarkMode(): boolean {
    return this.darkModeSubject.value;
  }

  /**
   * Get text size class name for CSS
   */
  getTextSizeClass(): string {
    return `text-size-${this.textSizeSubject.value}`;
  }

  /**
   * Get dark mode class name for CSS
   */
  getDarkModeClass(): string {
    // darkModeSubject.value true = dark mode enabled (no class needed - default)
    // darkModeSubject.value false = light mode enabled (add light-mode class)
    return this.darkModeSubject.value ? '' : 'light-mode';
  }

  /**
   * Get combined display classes
   */
  getDisplayClasses(): string {
    return `${this.getTextSizeClass()} ${this.getDarkModeClass()}`;
  }

  /**
   * Apply text size to document root and content elements
   */
  private applyTextSize(size: 1 | 2 | 3 | 4 | 5): void {
    const root = document.documentElement;
    
    // Remove existing text size classes from root
    root.classList.remove('text-size-1', 'text-size-2', 'text-size-3', 'text-size-4', 'text-size-5');
    
    // Add new text size class to root
    root.classList.add(`text-size-${size}`);
    
    // Set CSS custom properties for dynamic sizing (immediate effect)
    const fontSizeMultiplier = this.getFontSizeMultiplier(size);
    root.style.setProperty('--text-size-multiplier', fontSizeMultiplier.toString());
    root.style.setProperty('--base-font-size', `${fontSizeMultiplier}rem`);
    root.style.setProperty('--title-font-size', `${1.5 * fontSizeMultiplier}rem`);
    
    // Apply to ion-content elements for component-scoped styles (non-blocking)
    requestAnimationFrame(() => {
      const ionContents = document.querySelectorAll('ion-content');
      ionContents.forEach((content, index) => {
        content.classList.remove('text-size-1', 'text-size-2', 'text-size-3', 'text-size-4', 'text-size-5');
        content.classList.add(`text-size-${size}`);
      });
    });
  }

  /**
   * Apply dark mode to document and content elements
   */
  private applyDarkMode(enabled: boolean): void {
    const root = document.documentElement;
    
    if (enabled) {
      // Dark mode enabled - remove light mode class (use default styles)
      root.classList.remove('light-mode');
    } else {
      // Light mode enabled - add light mode class
      root.classList.add('light-mode');
    }
    
    // Apply to ion elements for component-scoped styles (non-blocking)
    requestAnimationFrame(() => {
      const ionElements = document.querySelectorAll('ion-content, ion-header, ion-toolbar');
      ionElements.forEach((element, index) => {
        if (enabled) {
          element.classList.remove('light-mode');
        } else {
          element.classList.add('light-mode');
        }
      });
    });
  }

  /**
   * Get font size multiplier based on text size preference
   */
  private getFontSizeMultiplier(size: 1 | 2 | 3 | 4 | 5): number {
    const multipliers = {
      1: 0.75,  // Smallest
      2: 0.875, // Small
      3: 1.0,   // Medium (default)
      4: 1.125, // Large
      5: 1.25   // Largest
    };
    return multipliers[size];
  }

  /**
   * Toggle dark mode on/off
   */
  toggleDarkMode(): boolean {
    const newValue = !this.darkModeSubject.value;
    this.setDarkMode(newValue);
    return newValue;
  }

  /**
   * Increase text size (up to maximum)
   */
  increaseTextSize(): 1 | 2 | 3 | 4 | 5 {
    const current = this.textSizeSubject.value;
    const newSize = Math.min(current + 1, 5) as 1 | 2 | 3 | 4 | 5;
    this.setTextSize(newSize);
    return newSize;
  }

  /**
   * Decrease text size (down to minimum)
   */
  decreaseTextSize(): 1 | 2 | 3 | 4 | 5 {
    const current = this.textSizeSubject.value;
    const newSize = Math.max(current - 1, 1) as 1 | 2 | 3 | 4 | 5;
    this.setTextSize(newSize);
    return newSize;
  }

  /**
   * Refresh styles (useful when DOM elements are added dynamically)
   */
  refreshStyles(): void {
    console.log('refreshStyles: Reapplying current styles');
    this.applyDarkMode(this.darkModeSubject.value);
    this.applyTextSize(this.textSizeSubject.value);
    
    // If no elements were found, retry after a short delay (reduced from 200ms to 50ms)
    const ionContents = document.querySelectorAll('ion-content');
    if (ionContents.length === 0) {
      console.log('refreshStyles: No ion-content found, retrying in 50ms');
      setTimeout(() => {
        this.applyDarkMode(this.darkModeSubject.value);
        this.applyTextSize(this.textSizeSubject.value);
      }, 50);
    }
  }
}